import { useCallback, useEffect, useRef, useState } from 'react'
import { socket } from 'features/Chat/ChatContext'

/**
 * Typing indicators for one thread.
 *
 * Deliberately NOT in ChatProvider: typist state changes several times a
 * second while someone is writing, and every `useChat()` consumer, including
 * the two Home dashboard cards, would re-render on each change. It lives in
 * the component that shows the dots.
 *
 * Outgoing emits are throttled by refs, never state, so typing does not
 * re-render the composer either.
 */

/** Re-announce at most this often while the user keeps typing. */
const EMIT_THROTTLE_MS = 3000
/** Silence for this long counts as stopped. */
const IDLE_STOP_MS = 4000
/** A remote typist expires this long after their last announcement, which
 *  covers a `typing_stop` that never arrived. */
const REMOTE_EXPIRY_MS = 6000

interface Typist {
	name: string
	expiresAt: number
}

export const useTypingIndicator = ({
	roomId,
	userId,
	name,
	enabled = true,
}: {
	roomId: string
	userId: string
	name: string
	enabled?: boolean
}) => {
	const [typists, setTypists] = useState<Map<string, Typist>>(new Map())
	const lastEmitAt = useRef(0)
	const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
	const isTyping = useRef(false)
	const roomIdRef = useRef(roomId)
	roomIdRef.current = roomId

	const emitStop = useCallback(() => {
		if (!isTyping.current || !roomIdRef.current) return
		isTyping.current = false
		lastEmitAt.current = 0
		socket.emit('typing_stop', { roomId: roomIdRef.current, userId })
	}, [userId])

	/** Call on every keystroke. Handles its own throttling and idle timeout. */
	const notifyTyping = useCallback(() => {
		if (!enabled || !roomIdRef.current || !userId) return

		const now = Date.now()
		if (!isTyping.current || now - lastEmitAt.current > EMIT_THROTTLE_MS) {
			isTyping.current = true
			lastEmitAt.current = now
			socket.emit('typing_start', { roomId: roomIdRef.current, userId, name })
		}

		if (idleTimer.current) clearTimeout(idleTimer.current)
		idleTimer.current = setTimeout(emitStop, IDLE_STOP_MS)
	}, [enabled, userId, name, emitStop])

	/** Call when the message goes out. */
	const stopTyping = useCallback(() => {
		if (idleTimer.current) clearTimeout(idleTimer.current)
		emitStop()
	}, [emitStop])

	// Remote typists.
	useEffect(() => {
		const onTyping = (payload: any) => {
			if (!payload || payload.roomId !== roomIdRef.current) return
			if (String(payload.userId) === String(userId)) return
			setTypists(prev => {
				const next = new Map(prev)
				next.set(String(payload.userId), {
					name: payload.name || 'Someone',
					expiresAt: Date.now() + REMOTE_EXPIRY_MS,
				})
				return next
			})
		}

		const onStopped = (payload: any) => {
			if (!payload) return
			setTypists(prev => {
				if (!prev.has(String(payload.userId))) return prev
				const next = new Map(prev)
				next.delete(String(payload.userId))
				return next
			})
		}

		socket.on('user_typing', onTyping)
		socket.on('user_stopped_typing', onStopped)
		return () => {
			socket.off('user_typing', onTyping)
			socket.off('user_stopped_typing', onStopped)
		}
	}, [userId])

	// Prune expired typists once a second, so a dropped stop event cannot leave
	// a permanent ellipsis.
	useEffect(() => {
		const id = setInterval(() => {
			setTypists(prev => {
				const now = Date.now()
				let changed = false
				const next = new Map(prev)
				next.forEach((typist, key) => {
					if (typist.expiresAt <= now) {
						next.delete(key)
						changed = true
					}
				})
				return changed ? next : prev
			})
		}, 1000)
		return () => clearInterval(id)
	}, [])

	// Announce a stop when the thread changes, the popup closes, or the tab
	// goes away, rather than leaving the other side watching dots.
	useEffect(() => {
		setTypists(new Map())
		return () => {
			if (idleTimer.current) clearTimeout(idleTimer.current)
			emitStop()
		}
	}, [roomId, emitStop])

	useEffect(() => {
		window.addEventListener('beforeunload', emitStop)
		return () => window.removeEventListener('beforeunload', emitStop)
	}, [emitStop])

	return {
		typistNames: Array.from(typists.values()).map(t => t.name),
		notifyTyping,
		stopTyping,
	}
}

export default useTypingIndicator
